import {
  Badge,
  Button,
  GreyGrey30,
  GreyGrey50,
  GreyGrey80,
  GreyGrey90,
  IconArrowRight,
  IndigoIndigo60,
  Link,
  Primary12Regular,
  Primary12SemiBold,
  Primary13Regular,
  Primary13SemiBold,
  Primary16SemiBold,
  SequenceMenu,
  ShadowXs,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import {
  Dashboardv99990101CommonModels,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  toInvoicePaymentStatusBadgeProps,
  toInvoiceStatusBadgeProps
} from '@sequencehq/utils'
import {
  format,
  formatDateRange,
  parse,
  SECONDS
} from '@sequencehq/utils/dates'
import { useBillingScheduleId } from 'modules/BillingScheduleHome/utils/useBillingScheduleId'
import {
  type ListedInvoiceAmountBasis,
  toAmountBasis,
  toListedInvoiceAmount
} from 'Invoices/utils'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import {
  API_DATE_FORMAT,
  INVOICES_FOR_SCHEDULE_PARAMS
} from 'BillingScheduleHome/billingScheduleHome.constants'
import { useInvoiceDueDateFormatter } from 'Invoices/hooks/useInvoiceDueDateFormatter'
import fill from 'lodash/fill'
import { SectionError } from '../common/SectionError'

type InvoiceStatus = DashboardApi20240730.GetInvoice.InvoiceStatus
type InvoicePaymentStatus = DashboardApi20240730.GetInvoice.InvoicePaymentStatus
type APIInvoice =
  Dashboardv99990101CommonModels.Invoice.InvoiceWithoutDueDateProvenance

const INVOICE_TABLE_DATE_FORMAT = 'd MMM'

const INVOICE_CELL_LINK_STYLE = {
  display: 'block',
  color: 'inherit',
  textDecoration: 'none'
} as const

const formatInvoice = ({
  apiInvoice,
  basis
}: {
  apiInvoice: APIInvoice
  basis: ListedInvoiceAmountBasis
}): TableInvoice => {
  const accountingDate = parse(
    apiInvoice.accountingDate,
    API_DATE_FORMAT,
    new Date()
  )
  const sentDate = apiInvoice.issueDate
    ? parse(apiInvoice.issueDate, API_DATE_FORMAT, new Date())
    : undefined

  const billingPeriod = apiInvoice.billingPeriod
    ? {
        start: parse(
          apiInvoice.billingPeriod.start,
          API_DATE_FORMAT,
          new Date()
        ),
        endInclusive: parse(
          apiInvoice.billingPeriod.endInclusive,
          API_DATE_FORMAT,
          new Date()
        )
      }
    : undefined

  return {
    id: apiInvoice.id,
    amount: toListedInvoiceAmount({ invoice: apiInvoice, basis }),
    currency: apiInvoice.currency,
    status: apiInvoice.status,
    paymentStatus: apiInvoice.paymentStatus,
    invoiceNumber: apiInvoice.invoiceNumber,
    billingPeriod: billingPeriod
      ? formatDateRange({
          from: billingPeriod.start,
          to: billingPeriod.endInclusive
        })
      : '',
    dueDate: apiInvoice.dueDate,
    sentDate: sentDate && format(sentDate, INVOICE_TABLE_DATE_FORMAT),
    date: format(accountingDate, INVOICE_TABLE_DATE_FORMAT)
  }
}

const useInvoicesSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const { useCreditGrantLedger } = useFeatureFlags()
  const amountBasis = toAmountBasis(useCreditGrantLedger)
  const merchantSettingsQuery = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )
  // The v99990101 list is the one that reports the credit a grant has applied.
  const invoicesQuery = useQuery(
    dashboardv99990101Client.getInvoices,
    { billingScheduleId, ...INVOICES_FOR_SCHEDULE_PARAMS },
    {
      select: data => {
        if (!data) {
          return {
            invoices: []
          }
        }

        return {
          invoices: data.items.map(apiInvoice =>
            formatInvoice({ apiInvoice, basis: amountBasis })
          )
        }
      },
      refetchInterval: query => {
        if (query.state.dataUpdateCount <= 10) {
          return 2 * SECONDS
        }

        return 10 * SECONDS
      }
    }
  )

  return {
    isLoading: invoicesQuery.isPending || merchantSettingsQuery.isPending,
    isError: invoicesQuery.isError,
    invoices: invoicesQuery.data?.invoices ?? [],
    invoicePrefix: merchantSettingsQuery.data?.invoiceNumberPrefix ?? 'INV'
  }
}

type TableInvoice = {
  id: string
  amount: string
  currency: string
  status: InvoiceStatus
  paymentStatus: InvoicePaymentStatus
  invoiceNumber?: string
  billingPeriod: string
  dueDate?: string
  sentDate?: string
  date: string
}

const InvoiceKebabMenu = (props: { invoiceId: string }) => {
  return (
    <SequenceMenu
      variant="ghost"
      alignment="bottom-right"
      buttonSize="small"
      items={[
        ({ setIsOpen }) => (
          <Link.Unstyled key="view-invoice" to={`/invoices/${props.invoiceId}`}>
            <SequenceMenu.Item
              uuid="view-invoice"
              label="View invoice"
              data-testid={`schedule.invoices.${props.invoiceId}.menuItem.view`}
              onClick={() => setIsOpen?.(false)}
            />
          </Link.Unstyled>
        )
      ]}
    />
  )
}

const InvoicesTable = (props: {
  invoices: Array<TableInvoice>
  invoicePrefix: string
}) => {
  const formatDueDate = useInvoiceDueDateFormatter()

  return (
    <TableContainer
      borderRadius="lg"
      overflow="hidden"
      border={`1px solid ${GreyGrey30}`}
      width="100%"
      boxShadow={ShadowXs}
    >
      <Table variant="v2" width="100%">
        <Thead userSelect="none">
          <Tr>
            <Th width="140px" textAlign="right">
              Amount
            </Th>
            <Th width="54px"></Th>
            <Th width="140px"></Th>
            <Th width="100%">Invoice #</Th>
            <Th width="204px" textAlign="right">
              Billing period
            </Th>
            <Th textAlign="right" width="74px">
              Sent
            </Th>
            <Th textAlign="right" width="74px">
              Due
            </Th>
            <Th width="74px">Date</Th>
            <Th width="52px"></Th>
          </Tr>
        </Thead>
        {props.invoices.length ? (
          <Tbody>
            {props.invoices.map(invoice => {
              const to = `/invoices/${invoice.id}`
              const dueDateText = formatDueDate({
                dueDate: invoice.dueDate,
                formatDate: dueDate =>
                  format(
                    parse(dueDate, API_DATE_FORMAT, new Date()),
                    INVOICE_TABLE_DATE_FORMAT
                  )
              })

              return (
                <Tr key={invoice.id}>
                  <Td textAlign="right">
                    <Link.Unstyled
                      to={to}
                      style={INVOICE_CELL_LINK_STYLE}
                      data-testid={`schedule.invoices.${invoice.id}.row`}
                    >
                      {invoice.amount}
                    </Link.Unstyled>
                  </Td>
                  <Td>
                    <Link.Unstyled to={to} style={INVOICE_CELL_LINK_STYLE}>
                      {invoice.currency}
                    </Link.Unstyled>
                  </Td>
                  <Td padding="0 12px !important">
                    <Link.Unstyled to={to} style={INVOICE_CELL_LINK_STYLE}>
                      <Flex gap={2} alignItems="center">
                        <Badge
                          {...toInvoiceStatusBadgeProps({
                            status: invoice.status,
                            size: 'sm'
                          })}
                        />
                        {invoice.status === 'SENT' && (
                          <Badge
                            {...toInvoicePaymentStatusBadgeProps({
                              status: invoice.paymentStatus,
                              size: 'sm'
                            })}
                          />
                        )}
                      </Flex>
                    </Link.Unstyled>
                  </Td>
                  <Td>
                    <Link.Unstyled to={to} style={INVOICE_CELL_LINK_STYLE}>
                      {invoice.invoiceNumber ? (
                        <Box as="span" {...Primary12SemiBold}>
                          {invoice.invoiceNumber}
                        </Box>
                      ) : (
                        <Box as="span" {...Primary12Regular} color={GreyGrey50}>
                          {props.invoicePrefix}-DRAFT
                        </Box>
                      )}
                    </Link.Unstyled>
                  </Td>
                  <Td textAlign="right">
                    <Link.Unstyled to={to} style={INVOICE_CELL_LINK_STYLE}>
                      {invoice.billingPeriod}
                    </Link.Unstyled>
                  </Td>
                  <Td textAlign="right">
                    <Link.Unstyled to={to} style={INVOICE_CELL_LINK_STYLE}>
                      {invoice.sentDate}
                    </Link.Unstyled>
                  </Td>
                  <Td
                    textAlign="right"
                    data-testid={`schedule.invoices.${invoice.id}.dueDate`}
                  >
                    <Link.Unstyled to={to} style={INVOICE_CELL_LINK_STYLE}>
                      {dueDateText}
                    </Link.Unstyled>
                  </Td>
                  <Td>
                    <Link.Unstyled to={to} style={INVOICE_CELL_LINK_STYLE}>
                      {invoice.date}
                    </Link.Unstyled>
                  </Td>
                  <Td padding="0 12px !important">
                    <InvoiceKebabMenu invoiceId={invoice.id} />
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        ) : (
          <Tbody>
            <Tr>
              <Td colSpan={9} rowSpan={2} pointerEvents="none">
                <Flex
                  padding="16px"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  userSelect="none"
                >
                  <Box {...Primary13SemiBold} color={GreyGrey90}>
                    No invoices to display
                  </Box>
                  <Box {...Primary13Regular} color={GreyGrey80}>
                    No invoices have been created for this schedule yet.
                  </Box>
                </Flex>
              </Td>
            </Tr>
          </Tbody>
        )}
      </Table>
    </TableContainer>
  )
}

export const ScheduleInvoicesSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const { invoices, isError, isLoading, invoicePrefix } = useInvoicesSection()

  if (isLoading) {
    return (
      <Box>
        <Box {...Primary16SemiBold} marginBottom="16px">
          Invoices
        </Box>
        <Flex gap="8px" marginBottom="24px" flexDirection="column">
          {fill(Array(5), null).map((_, idx) => (
            <Skeleton height="36px" width="100%" key={idx} />
          ))}
          <Box marginTop="8px">
            <Skeleton height="32px" width="200px" />
          </Box>
        </Flex>
      </Box>
    )
  }

  if (isError) {
    return (
      <Box>
        <Box {...Primary16SemiBold} marginBottom="16px">
          Invoices
        </Box>
        <Box
          borderRadius="lg"
          padding="16px"
          overflow="hidden"
          border={`1px solid ${GreyGrey30}`}
        >
          <SectionError
            size="medium"
            text="An error occurred when loading the invoice information"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Flex flexDirection="column" gap="8px">
      <Box {...Primary16SemiBold} marginBottom="8px">
        Invoices
      </Box>
      <InvoicesTable invoices={invoices} invoicePrefix={invoicePrefix} />
      {Boolean(invoices.length) && (
        <Link.Unstyled to={`/invoices?billingScheduleId=${billingScheduleId}`}>
          <Button {...Primary13SemiBold} variant="ghost">
            <Flex alignItems="center" gap="8px">
              View all invoices
              <IconArrowRight color={IndigoIndigo60} />
            </Flex>
          </Button>
        </Link.Unstyled>
      )}
    </Flex>
  )
}
